import {
  openAuthPopup,
  POPUP_AUTH_CALLBACK_QUERY_PARAM_NAME,
} from "./popupAuth.js";
import { getSupabaseClient } from "./supabaseClient.js";

/**
 * Sends a Supabase email magic link, returning the user to the current URL
 * (minus the hash) after they click it. Magic-link emails open in the user's
 * regular browser, so no popup-auth callback param is included. Shared by the
 * standalone sign-in screen and the invitation sign-in screen.
 */
export async function sendMagicLinkSignIn(
  email: string,
): Promise<{ error: string | null }> {
  const emailRedirectToUrl = new URL(window.location.href);
  emailRedirectToUrl.hash = "";

  const { error } = await getSupabaseClient().auth.signInWithOtp({
    email,
    options: { emailRedirectTo: emailRedirectToUrl.toString() },
  });

  return { error: error?.message ?? null };
}

/**
 * Launches SSO for a known provider id (one click, no domain entry). Returns to
 * the current URL (minus the hash) afterwards so an invitation deep link's token
 * survives the round-trip. Shared by the invitation sign-in screen and the
 * account-resolution mismatch screen.
 *
 * In popup mode (embedded iframe auth, e.g. Fountain) the IdP opens in a popup
 * that posts the auth code back to the opener; otherwise the browser redirects.
 */
export async function startSsoSignInWithProviderId(
  providerId: string,
  { usePopup }: { usePopup: boolean },
): Promise<{ error: string | null }> {
  const redirectTo = new URL(window.location.href);
  redirectTo.hash = "";
  // In popup mode, flag the callback URL so the popup knows to send the code
  // back to the parent window instead of establishing a session itself.
  if (usePopup) {
    redirectTo.searchParams.set(POPUP_AUTH_CALLBACK_QUERY_PARAM_NAME, "true");
  }

  const { data, error } = await getSupabaseClient().auth.signInWithSSO({
    providerId,
    options: { redirectTo: redirectTo.toString() },
  });

  if (error || !data?.url) {
    return { error: "Couldn't start SSO sign-in. Please try again." };
  }

  if (usePopup) {
    openAuthPopup(data.url);
  } else {
    window.location.href = data.url;
  }
  return { error: null };
}
